import React from 'react';
import styled from 'styled-components';

export interface ProgressStep {
  label: string;
  active: boolean;
  date?: string;
  tooltip?: string;
}

type StepPosition = 'first' | 'middle' | 'last';

const ProgressBox = styled.div`
  border-radius: 8px;
  padding: 12px 0;
`;

const StepsContainer = styled.div`
  display: flex;
  width: 100%;
`;

const StepCol = styled.div<{ position: StepPosition }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: ${({ position }) => (position === 'first' ? 'flex-start' : position === 'last' ? 'flex-end' : 'center')};
  min-width: 0;
`;

const StepLabelText = styled.span<{ position: StepPosition }>`
  font-size: 12px;
  font-weight: 400;
  color: #666;
  margin-bottom: 6px;
  text-align: ${({ position }) => (position === 'first' ? 'left' : position === 'last' ? 'right' : 'center')};
  word-break: break-word;
  @media (max-width: 1024px) {
    font-size: 10px;
  }
`;

const CircleRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 22px;
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover > span {
    display: block;
  }
`;

const TooltipBox = styled.span`
  display: none;
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: #fff;
  font-size: 11px;
  white-space: nowrap;
  padding: 4px 8px;
  border-radius: 4px;
  z-index: 10;
  pointer-events: none;
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #333;
  }
`;

const CircleDot = styled.div<{ active: boolean }>`
  width: 22px;
  height: 22px;
  min-width: 22px;
  border-radius: 50%;
  background: ${({ active }) => (active ? '#3994de' : '#9e9e9e')};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  @media (max-width: 1024px) {
    width: 18px;
    height: 18px;
    min-width: 18px;
  }
`;

const Checkmark = styled.span`
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
`;

const LineSegment = styled.div<{ active: boolean }>`
  flex: 1;
  height: 3px;
  background: ${({ active }) => (active ? '#3994de' : '#9e9e9e')};
`;

const StepDateText = styled.span<{ position: StepPosition }>`
  font-size: 12px;
  font-weight: 400;
  color: #000;
  margin-top: 6px;
  text-align: ${({ position }) => (position === 'first' ? 'left' : position === 'last' ? 'right' : 'center')};
  min-height: 16px;
  @media (max-width: 1024px) {
    font-size: 10px;
  }
`;

const Notes = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const getStepPosition = (i: number, total: number): StepPosition => {
  if (i === 0) return 'first';
  if (i === total - 1) return 'last';
  return 'middle';
};

interface ProgressStepperProps {
  steps: ProgressStep[];
  notes?: string;
}

const ProgressStepper = ({ steps, notes }: ProgressStepperProps) => (
  <>
    <ProgressBox>
      <StepsContainer>
        {steps.map((step, i) => {
          const position = getStepPosition(i, steps.length);
          return (
            <StepCol key={i} position={position}>
              <StepLabelText position={position}>{step.label}</StepLabelText>
              <CircleRow>
                {i > 0 && <LineSegment active={step.active} />}
                <TooltipWrapper>
                  <CircleDot active={step.active}>{step.active && <Checkmark>✓</Checkmark>}</CircleDot>
                  {step.tooltip && <TooltipBox>{step.tooltip}</TooltipBox>}
                </TooltipWrapper>
                {i < steps.length - 1 && <LineSegment active={steps[i + 1].active} />}
              </CircleRow>
              <StepDateText position={position}>{step.date ?? ''}</StepDateText>
            </StepCol>
          );
        })}
      </StepsContainer>
    </ProgressBox>
    {notes && <Notes>{notes}</Notes>}
  </>
);

export default ProgressStepper;
